<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { watch } from 'vue'
import { popupUtil } from '@/utils/Popup'

const props = defineProps<{
  modelValue: string
}>()

const emit = defineEmits(['update:modelValue'])

const editor = useEditor({
  extensions: [StarterKit, Link, Underline],
  content: props.modelValue,
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  }
})

watch(
  () => props.modelValue,
  (value) => {
    const isSame = editor.value?.getHTML() === value
    if (isSame) return

    editor.value?.commands.setContent(value, false)
  }
)

const unsetLink = () => {
  editor.value!.chain().focus().extendMarkRange('link').unsetLink().run()
}

const setLink = async () => {
  const url = await popupUtil.prompt('Enter the URL')

  if (url) {
    editor.value!.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
}
</script>

<template>
  <div class="editor">
    <div class="editor-toolbar">
      <button type="button" @click="editor!.chain().focus().undo().run()" id="undo">
        <span class="material-symbols-outlined">undo</span>
      </button>
      <button type="button" @click="editor!.chain().focus().redo().run()" id="redo">
        <span class="material-symbols-outlined">redo</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleBlockquote().run()" id="blockquote">
        <span :class="{ active: editor?.isActive('blockquote') }" class="material-symbols-outlined">format_quote</span>
      </button>
      <button type="button" @click="setLink" v-if="!editor?.isActive('link')" id="link-on">
        <span class="material-symbols-outlined">link</span>
      </button>
      <button type="button" @click="unsetLink" v-else id="link-off">
        <span class="material-symbols-outlined">link_off</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleUnderline().run()" id="underline">
        <span :class="{ active: editor?.isActive('underline') }" class="material-symbols-outlined">format_underlined</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleStrike().run()" id="strike">
        <span :class="{ active: editor?.isActive('strike') }" class="material-symbols-outlined">format_strikethrough</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleItalic().run()" id="italic">
        <span :class="{ active: editor?.isActive('italic') }" class="material-symbols-outlined">format_italic</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleBold().run()" id="bold">
        <span :class="{ active: editor?.isActive('bold') }" class="material-symbols-outlined">format_bold</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleBulletList().run()" id="bullet-list">
        <span :class="{ active: editor?.isActive('bulletList') }" class="material-symbols-outlined">list</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleOrderedList().run()" id="ordered-list">
        <span :class="{ active: editor?.isActive('orderedList') }" class="material-symbols-outlined">format_list_numbered</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleHeading({ level: 1 }).run()" id="heading-1">
        <span :class="{ active: editor?.isActive('heading', { level: 1 }) }" class="material-symbols-outlined">format_h1</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleHeading({ level: 2 }).run()" id="heading-2">
        <span :class="{ active: editor?.isActive('heading', { level: 2 }) }" class="material-symbols-outlined">format_h2</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleHeading({ level: 3 }).run()" id="heading-3">
        <span :class="{ active: editor?.isActive('heading', { level: 3 }) }" class="material-symbols-outlined">format_h3</span>
      </button>
      <button type="button" @click="editor!.chain().focus().toggleCode().run()" id="code">
        <span :class="{ active: editor?.isActive('code') }" class="material-symbols-outlined">code</span>
      </button>
    </div>

    <editor-content class="editor-content" :editor="editor" />
  </div>
</template>

<style scoped lang="scss">
.editor {
  display: flex;
  flex-direction: column;

  .editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    background-color: #2253a0;
    border-radius: 8px 8px 0 0;

    button {
      display: flex;
      justify-content: stretch;
      align-items: stretch;
      padding: 0;

      width: 36px;
      height: 36px;
      border: none;
      background-color: #2253a0;
      color: #eee;
      font-size: 1.2rem;
      cursor: pointer;

      transition: background-color 0.2s;
      transform-origin: center;

      span {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
      }

      span.active {
        background-color: #1a3f7f;
      }

      &:hover {
        background-color: #1a3f7f;
      }

      &:active {
        transform: scale(0.9);
      }
    }
  }

  .editor-content {
    border: 1px solid var(--theme-border-color);
    height: 200px;
    border-radius: 0 0 8px 8px;
    padding: 8px;
    overflow-y: auto;
    background-color: var(--theme-background);
  }
}
</style>
